import { isNil } from 'lodash';
import { useContext } from 'react';
import { Menu, useMenuContext } from '@shared/components/Menu';
import { ASK_MARK_ICON } from '@shared/scenes/ask';
import { MBSelectAttachmentType } from '@shared/scenes/upload/util/types';
import { grid } from '@shared/styles';
import { MBAnalytics } from '@shared/util/analytics';
import { i18n } from '@shared/util/i18n';
import { Can, ChannelPermissionsContext } from '@shared/util/permissions';
import styles from './PlusButton2.module.css';

interface PlusButtonProps {
  draftId: string;
  urgent: boolean;
  hideUrgent?: boolean;
  /** Omitted where there is no workspace to ask about, which hides the Ask item. */
  onAsk?: () => void;
  onCreateCard: () => void;
  onShowGIFModal: () => void;
  onSelectAttachment: (type: MBSelectAttachmentType) => void;
  onSendAsUrgent: () => void;
  onRemoveSendAsUrgent: () => void;
}

export function PlusButton({
  draftId,
  urgent,
  hideUrgent,
  onAsk,
  onCreateCard,
  onShowGIFModal,
  onSelectAttachment,
  onSendAsUrgent,
  onRemoveSendAsUrgent,
}: PlusButtonProps) {
  const channelPermissions = useContext(ChannelPermissionsContext);
  const channelAbility = channelPermissions.channelAbility;

  const handleSendAsUrgent = () => {
    onSendAsUrgent();
    MBAnalytics.track('Chat::Input::PlusButton::SendAsUrgent', { draftId });
  };

  const onClickCreate = () => {
    onCreateCard();
    MBAnalytics.track('Chat::Input::PlusButton::CreateCard', { draftId });
  };

  const handleRemoveSendAsUrgent = () => {
    onRemoveSendAsUrgent();
    MBAnalytics.track('Chat::Input::PlusButton::RemoveSendAsUrgent', {
      draftId,
    });
  };

  const handleAsk = () => {
    onAsk?.();
    MBAnalytics.track('Chat::Input::PlusButton::Ask', { draftId });
  };

  const handleShowGIFModal = () => {
    onShowGIFModal();
    MBAnalytics.track('Chat::Input::PlusButton::ShowGIFModal', { draftId });
  };

  const handleSelectAttachment = () => {
    onSelectAttachment('all');
    MBAnalytics.track('Chat::Input::PlusButton::SelectAttachment', { draftId });
  };

  const onOpenChange = (open: boolean) => {
    if (open) {
      MBAnalytics.track('Chat::Input::PlusButton::Show', { draftId });
    }
  };

  return (
    <Menu onOpenChange={onOpenChange}>
      <PlusMenuButton />
      <Menu.Content>
        {/* Ask goes to the agent, everything below goes to the workspace — a separator rather
            than a heading, since the rest of the menu has never needed one. */}
        {!isNil(onAsk) && (
          <>
            <Menu.Item icon={ASK_MARK_ICON} label={i18n('ask.title')} onClick={handleAsk} />
            <Menu.Separator />
          </>
        )}
        <Can I="create" a="Card" ability={channelAbility}>
          <Menu.Item
            icon="card"
            label={i18n('chat.input.plusButton.createCard')}
            onClick={onClickCreate}
          />
        </Can>
        <Menu.Item
          icon="imagestack"
          label={i18n('chat.input.plusButton.addImageOrFile')}
          onClick={handleSelectAttachment}
        />
        <Menu.Item
          icon="gif"
          label={i18n('chat.input.plusButton.gifSearch')}
          onClick={handleShowGIFModal}
        />

        {!hideUrgent && !urgent && (
          <Menu.Item
            icon="clock"
            label={i18n('chat.input.plusButton.sendAsUrgent')}
            onClick={handleSendAsUrgent}
          />
        )}

        {!hideUrgent && !!urgent && (
          <Menu.Item
            icon="clock"
            label={i18n('chat.input.plusButton.removeSendAsUrgent')}
            onClick={handleRemoveSendAsUrgent}
            danger
          />
        )}
      </Menu.Content>
    </Menu>
  );
}

function PlusMenuButton() {
  const { isOpen } = useMenuContext();
  return (
    <Menu.Button
      variant={isOpen ? 'primary' : 'secondary'}
      size="medium"
      iconOnly
      icon="plus"
      iconSize={grid(3)}
      className={styles.plusButton}
    />
  );
}
